import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Feedback {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  type: "sugestao" | "problema" | "elogio" | "outro";
  subject: string;
  message: string;
  status: "pendente" | "lido" | "arquivado";
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useFeedbacks() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  // For users: submit feedback
  const submitFeedback = useCallback(async (data: {
    user_id: string;
    user_name: string;
    user_email: string;
    type: string;
    subject: string;
    message: string;
  }) => {
    try {
      setSubmitting(true);
      const { error } = await supabase.from("feedbacks").insert({
        user_id: data.user_id,
        user_name: data.user_name,
        user_email: data.user_email,
        type: data.type,
        subject: data.subject,
        message: data.message,
      });

      if (error) throw error;

      toast({
        title: "Feedback enviado!",
        description: "Obrigado pela sua contribuição. Analisaremos em breve.",
      });

      return { success: true };
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Erro ao enviar feedback",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setSubmitting(false);
    }
  }, [toast]);

  // For admins: fetch all feedbacks
  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("feedbacks")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFeedbacks((data as Feedback[]) || []);
    } catch (error: any) {
      console.error("Error fetching feedbacks:", error);
      toast({
        title: "Erro ao carregar feedbacks",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // For admins: update feedback status
  const updateFeedbackStatus = useCallback(async (id: string, status: string, adminNotes?: string) => {
    try {
      const updateData: Record<string, string> = { status };
      if (adminNotes !== undefined) {
        updateData.admin_notes = adminNotes;
      }

      const { error } = await supabase
        .from("feedbacks")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      setFeedbacks((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status: status as Feedback["status"], admin_notes: adminNotes ?? f.admin_notes } : f))
      );

      toast({
        title: "Status atualizado",
        description: "O feedback foi atualizado com sucesso.",
      });

      return { success: true };
    } catch (error: any) {
      console.error("Error updating feedback:", error);
      toast({
        title: "Erro ao atualizar feedback",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
      return { success: false, error };
    }
  }, [toast]);

  // For admins: delete feedback
  const deleteFeedback = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from("feedbacks")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setFeedbacks((prev) => prev.filter((f) => f.id !== id));

      toast({
        title: "Feedback excluído",
        description: "O feedback foi removido com sucesso.",
      });

      return { success: true };
    } catch (error: any) {
      console.error("Error deleting feedback:", error);
      toast({
        title: "Erro ao excluir feedback",
        description: error.message || "Tente novamente mais tarde.",
        variant: "destructive",
      });
      return { success: false, error };
    }
  }, [toast]);

  return {
    feedbacks,
    loading,
    submitting,
    submitFeedback,
    fetchFeedbacks,
    updateFeedbackStatus,
    deleteFeedback,
  };
}
